import { useEffect, useReducer } from 'react';
import {
  AGENT_SESSION_CHANGE_EVENT,
  getAgentToken,
  getAgentTokenExpiry,
  isAgentSessionExpired,
} from '../agentSession';

const TICK_MS = 1000;

export interface IAgentSession {
  isAgentSession: boolean;
  remainingSeconds?: number;
  isExpired: boolean;
}

// Live view of the support agent session: re-renders every second for the countdown and when the
// session is ended early (expireAgentSession).
export function useAgentSession(): IAgentSession {
  // A counter, not the time: a same-millisecond Date.now() would let React skip the re-render.
  const [, rerender] = useReducer((renders: number) => renders + 1, 0);
  const now = Date.now();
  const token = getAgentToken();
  const expiresAt = getAgentTokenExpiry(token);
  const isExpired = !!token && isAgentSessionExpired(now);

  useEffect(() => {
    window.addEventListener(AGENT_SESSION_CHANGE_EVENT, rerender);
    const interval = token && !isExpired ? window.setInterval(rerender, TICK_MS) : undefined;
    return () => {
      window.removeEventListener(AGENT_SESSION_CHANGE_EVENT, rerender);
      window.clearInterval(interval);
    };
  }, [token, isExpired]);

  return {
    isAgentSession: !!token,
    remainingSeconds: expiresAt === undefined ? undefined : Math.max(0, Math.floor((expiresAt - now) / 1000)),
    isExpired,
  };
}
